import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { Button, Badge } from "reactstrap";
import Loader from "../../common/Loader";
import { getAuctionGridLayout, saveAuctionGridLayout } from "../../../store/auctions/actions";
import { getLotLists } from "../../../store/auctions/lotActions";

import "./WebsiteDesign.css";

// Limites da grelha do catálogo do website.
// GRID_MAX_COLS tem de acompanhar o `grid-template-columns: repeat(N, 1fr)`
// de WebsiteDesign.css — um lote nunca pode ocupar mais colunas do que a grelha tem.
export const GRID_MAX_COLS = 5;
export const GRID_MAX_ROWS = 2;

const range = (n) => Array.from({ length: n }, (_, i) => i + 1);

// Lot Item Component
const LotItem = ({ lot, onSpanChange, isSelected, onSelect }) => {
  const style = {
    gridColumn: `span ${lot.col_span || 1}`,
    gridRow: `span ${lot.row_span || 1}`,
  };

  return (
    <div
      style={style}
      className={`grid-designer-item ${isSelected ? "selected" : ""}`}
      data-testid={`grid-item-${lot.id}`}
      onClick={() => onSelect(lot.id)}
    >
      <div className="grid-item-image-container">
        {lot.thumbnail ? (
          <img
            src={lot.thumbnail}
            alt={`Lot ${lot.lot_no}`}
            className="grid-item-thumbnail"
          />
        ) : (
          <div className="grid-item-no-image">
            <i className="ri-image-line" />
          </div>
        )}
      </div>
      <div className="grid-item-info">
        <span className="grid-item-lot-no">
          {lot.lot_no}{lot.lot_alpha || ""}
        </span>
        <span className="grid-item-title" title={lot.title}>
          {lot.title || "Untitled"}
        </span>
      </div>
      <Badge
        color="info"
        className="grid-item-span-badge"
      >
        {lot.col_span || 1}x{lot.row_span || 1}
      </Badge>
      {isSelected && (
        <div className="grid-item-controls" onClick={(e) => e.stopPropagation()}>
          <div className="span-control">
            <label>Cols:</label>
            <div className="span-buttons" data-testid="col-span-buttons">
              {range(GRID_MAX_COLS).map((val) => (
                <button
                  key={`col-${val}`}
                  data-testid={`col-span-${val}`}
                  className={`span-btn ${lot.col_span === val ? "active" : ""}`}
                  onClick={() => onSpanChange(lot.id, "col_span", val)}
                >
                  {val}
                </button>
              ))}
            </div>
          </div>
          <div className="span-control">
            <label>Rows:</label>
            <div className="span-buttons" data-testid="row-span-buttons">
              {range(GRID_MAX_ROWS).map((val) => (
                <button
                  key={`row-${val}`}
                  data-testid={`row-span-${val}`}
                  className={`span-btn ${lot.row_span === val ? "active" : ""}`}
                  onClick={() => onSpanChange(lot.id, "row_span", val)}
                >
                  {val}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const WebsiteDesign = ({ auctionId, t }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lots, setLots] = useState([]);
  const [selectedLotId, setSelectedLotId] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Sort helper: ascending by lot_no then lot_alpha
  const sortByLotNo = (a, b) => {
    const numA = a.lot_no || 0;
    const numB = b.lot_no || 0;
    if (numA !== numB) return numA - numB;
    return (a.lot_alpha || "").localeCompare(b.lot_alpha || "");
  };

  // Fetch lots and grid layout data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch all lots for this auction
      const lotsRes = await dispatch(
        getLotLists({
          page: 1,
          limit: 9999,
          search: "",
          sort: "lot_no",
          filter: JSON.stringify({ auction: auctionId }),
          exclude: JSON.stringify({}),
        })
      );

      const lotsData = lotsRes?.data?.data?.results || [];

      // Fetch existing grid layout
      const layoutData = await dispatch(getAuctionGridLayout(auctionId));

      // Merge lots with layout data, filter out lots without a lot_no
      let mergedLots = lotsData
        .filter((lot) => lot.lot_no != null)
        .map((lot) => {
          const existingLayout = layoutData
            ? layoutData.find((l) => l.auction_lot_id === lot.id)
            : null;

          return {
            id: lot.id,
            lot_no: lot.lot_no,
            lot_alpha: lot.lot_alpha || "",
            title: lot.contract_item?.title_en || lot.contract_item?.title_pt || "",
            thumbnail: lot.contract_item?.default_photo || null,
            col_span: existingLayout?.col_span || 1,
            row_span: existingLayout?.row_span || 1,
          };
        });

      // Always sort ascending by lot_no, then lot_alpha
      mergedLots.sort(sortByLotNo);

      setLots(mergedLots);
      setHasChanges(false);
    } catch (error) {
      console.error("Error fetching grid layout data:", error);
    } finally {
      setLoading(false);
    }
  }, [dispatch, auctionId]);

  useEffect(() => {
    if (auctionId) {
      fetchData();
    }
  }, [auctionId, fetchData]);

  // Handle span change
  const handleSpanChange = (lotId, field, value) => {
    setLots((prevLots) =>
      prevLots.map((lot) =>
        lot.id === lotId ? { ...lot, [field]: value } : lot
      )
    );
    setHasChanges(true);
  };

  // Handle lot selection
  const handleSelectLot = (lotId) => {
    setSelectedLotId((prev) => (prev === lotId ? null : lotId));
  };

  // Save layout
  const handleSave = async () => {
    setSaving(true);
    try {
      const layouts = lots.map((lot, index) => ({
        auction_lot_id: lot.id,
        grid_order: index,
        col_span: lot.col_span,
        row_span: lot.row_span,
      }));

      await dispatch(saveAuctionGridLayout(auctionId, layouts));
      setHasChanges(false);
    } catch (error) {
      console.error("Error saving grid layout:", error);
    } finally {
      setSaving(false);
    }
  };

  // Reset to default (all 1x1)
  const handleReset = () => {
    setLots((prevLots) =>
      prevLots.map((lot) => ({
        ...lot,
        col_span: 1,
        row_span: 1,
      }))
    );
    setSelectedLotId(null);
    setHasChanges(true);
  };

  if (loading) return <Loader />;

  return (
    <div className="website-design-container">
      {/* Toolbar */}
      <div className="grid-designer-toolbar">
        <div className="toolbar-left">
          <h5 className="mb-0">{t("common.websiteDesign")}</h5>
          <span className="text-muted ms-3">
            {lots.length} {t("common.lots").toLowerCase()}
          </span>
        </div>
        <div className="toolbar-right">
          <div className="toolbar-legend">
            <span className="legend-item">
              <i className="ri-cursor-line me-1" />
              Click a lot to resize
            </span>
          </div>
          <Button
            color="secondary"
            size="sm"
            onClick={handleReset}
            className="me-2"
          >
            <i className="ri-refresh-line me-1" />
            Reset
          </Button>
          <Button
            color="primary"
            size="sm"
            onClick={handleSave}
            disabled={!hasChanges || saving}
          >
            {saving ? (
              <>
                <i className="ri-loader-4-line ri-spin me-1" />
                Saving...
              </>
            ) : (
              <>
                <i className="ri-save-line me-1" />
                {t("common.save")} Layout
              </>
            )}
          </Button>
        </div>
      </div>

      {hasChanges && (
        <div className="unsaved-changes-banner">
          <i className="ri-error-warning-line me-2" />
          You have unsaved changes
        </div>
      )}

      {/* Grid Designer */}
      <div className="grid-designer-wrapper">
        <div className="grid-designer-grid">
          {lots.map((lot) => (
            <LotItem
              key={lot.id}
              lot={lot}
              onSpanChange={handleSpanChange}
              isSelected={selectedLotId === lot.id}
              onSelect={handleSelectLot}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default withTranslation()(WebsiteDesign);
